import { useQuery, useQueries } from '@tanstack/react-query';
import { publicClient } from '../services/amplifyClient';
import { computeTopOpportunitiesClient } from '../services/clientForecast';

/**
 * Hook for fetching top predictions (Top 10 opportunities)
 *
 * Features:
 * - Fetches Top10Opportunity records for a specific date
 * - Falls back to client-side Open-Meteo scoring if AppSync is unavailable
 * - Regional filtering (USA vs Global based on country)
 * - 15-minute stale time (predictions don't change frequently)
 * - Automatic sorting by rank
 *
 * Usage:
 *   const { predictions, isLoading } = usePredictions('2024-01-15');
 *   const { predictions: usaPredictions } = usePredictions('2024-01-15', 'USA');
 */

export type PredictionRegion = 'ALL' | 'USA' | 'GLOBAL';
export type PredictionType = 'SUNRISE' | 'SUNSET';

interface UsePredictionsOptions {
  region?: PredictionRegion;
  type?: PredictionType;
  enabled?: boolean;
}

/** Normalize Amplify model rows + client fallback into a shared shape for UI. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PredictionRow = Record<string, any>;

async function fetchPredictionsWithFallback(
  date: string,
  region: PredictionRegion,
  type?: PredictionType
): Promise<{ predictions: PredictionRow[]; usedFallback: boolean }> {
  try {
    // Use the GSI query (byDateRank) for efficient retrieval
    const { data, errors } = await publicClient.models.Top10Opportunity.listTop10OpportunityByDateAndRank({
      date,
    });

    if (errors && errors.length > 0) {
      throw new Error(`Failed to fetch predictions: ${errors[0].message}`);
    }

    let filtered = (data || []) as PredictionRow[];

    if (region === 'USA') {
      filtered = filtered.filter((p) => p.country === 'USA');
    } else if (region === 'GLOBAL') {
      filtered = filtered.filter((p) => p.country !== 'USA');
    }

    if (type) {
      filtered = filtered.filter((p) => p.type === type);
    }

    if (filtered.length > 0) {
      return { predictions: filtered, usedFallback: false };
    }

    // Empty backend table for this date — compute live client-side
    const fallback = await computeTopOpportunitiesClient({ date, region, type });
    return { predictions: fallback as PredictionRow[], usedFallback: true };
  } catch {
    // AppSync API key expired / unauthorized / network — client fallback
    const fallback = await computeTopOpportunitiesClient({ date, region, type });
    return { predictions: fallback as PredictionRow[], usedFallback: true };
  }
}

export function usePredictions(
  date: string,
  options: UsePredictionsOptions = {}
) {
  const {
    region = 'ALL',
    type,
    enabled = true,
  } = options;

  const queryKey = ['predictions', date, region, type];

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => fetchPredictionsWithFallback(date, region, type),
    enabled: enabled && !!date,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 30 * 60 * 1000, // Cache for 30 minutes
    retry: 1,
  });

  const predictions = data?.predictions ?? [];

  return {
    predictions,
    isLoading,
    error,
    refetch,
    isEmpty: !isLoading && predictions.length === 0,
    usedFallback: data?.usedFallback ?? false,
  };
}

/**
 * Hook for fetching predictions for today
 * Convenience wrapper around usePredictions
 *
 * Usage:
 *   const { predictions, isLoading } = useTodaysPredictions();
 */
export function useTodaysPredictions(options: UsePredictionsOptions = {}) {
  const today = new Date().toISOString().split('T')[0];
  return usePredictions(today, options);
}

/**
 * Hook for fetching predictions for a specific location
 * Finds the closest prediction to the given coordinates
 *
 * Usage:
 *   const { prediction, isLoading } = useLocationPrediction(date, 40.7, -74.0);
 */
export function useLocationPrediction(
  date: string,
  latitude: number,
  longitude: number,
  options: Omit<UsePredictionsOptions, 'region'> = {}
) {
  const { predictions, isLoading, error } = usePredictions(date, options);

  // Find closest prediction by calculating distance
  const closestPrediction = predictions.reduce((closest, current) => {
    if (!closest) return current;

    const closestDistance = calculateDistance(
      latitude,
      longitude,
      closest.latitude,
      closest.longitude
    );

    const currentDistance = calculateDistance(
      latitude,
      longitude,
      current.latitude,
      current.longitude
    );

    return currentDistance < closestDistance ? current : closest;
  }, null as typeof predictions[0] | null);

  return {
    prediction: closestPrediction,
    isLoading,
    error,
    notFound: !isLoading && !closestPrediction,
  };
}

/**
 * Hook for fetching predictions for multiple dates
 * Useful for weekly forecasts
 *
 * Usage:
 *   const { predictionsByDate, isLoading } = usePredictionRange(['2024-01-15', '2024-01-16']);
 */
export function usePredictionRange(
  dates: string[],
  options: UsePredictionsOptions = {}
) {
  const { region = 'ALL', type, enabled = true } = options;

  // Use useQueries to properly call multiple queries without violating hooks rules
  const queries = useQueries({
    queries: dates.map((date) => ({
      queryKey: ['predictions', date, region, type],
      queryFn: async () => {
        const result = await fetchPredictionsWithFallback(date, region, type);
        return result.predictions;
      },
      enabled: enabled && !!date,
      staleTime: 15 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      retry: 1,
    })),
  });

  // Build predictions map from query results
  type PredictionData = NonNullable<(typeof queries)[number]['data']>;
  const predictionsByDate: Record<string, PredictionData> = {};
  dates.forEach((date, index) => {
    predictionsByDate[date] = queries[index]?.data || [];
  });

  const isLoading = queries.some((q) => q.isLoading);
  const error = queries.find((q) => q.error)?.error || null;

  return {
    predictionsByDate,
    isLoading,
    error,
    refetchAll: () => queries.forEach((q) => q.refetch()),
  };
}

/**
 * Calculate distance between two points using Haversine formula
 * Returns distance in kilometers
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
